import React, { useState, ReactNode, useEffect, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { WebSocketContext } from '.';

interface WebSocketProviderProps {
  children: ReactNode;
  walletId: string;
}

/**
 * WebSocketProvider component
 *
 * @param {*} { children, walletId }
 * @return {*}
 */
export const WebSocketProvider: React.FC<WebSocketProviderProps> = ({ children, walletId }) => {
  const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
  const [socketUrl] = useState(`${protocol}://${window.location.host}/ws`);

  const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
    shouldReconnect: (_closeEvent) => true,
    reconnectAttempts: 60,
    reconnectInterval: 500
  });

  const alivePingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (readyState === 1 && !alivePingIntervalRef.current) {
      alivePingIntervalRef.current = setInterval(() => {
        sendMessage(JSON.stringify({ type: 'ping' }));
      }, 120000);
    }
    return () => {
      if (alivePingIntervalRef.current) {
        clearInterval(alivePingIntervalRef.current);
        alivePingIntervalRef.current = null;
      }
    };
  }, [readyState, sendMessage]);

  const connectionStatus =
    {
      0: 'Connecting',
      1: 'Open',
      2: 'Closing',
      3: 'Closed'
    }[readyState as 0 | 1 | 2 | 3] || 'Unknown';

  useEffect(() => {
    try {
      if (readyState === 1) {
        const message = {
          walletId: walletId,
          message: 'Connected!!'
        };
        sendMessage(JSON.stringify(message));
      }
    } catch (error) {
      console.error(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyState, sendMessage]);

  return (
    <WebSocketContext.Provider value={{ sendMessage, lastMessage, connectionStatus }}>
      {children}
    </WebSocketContext.Provider>
  );
};
